// etch-stamp.js — the Stamp: an icon's etch-and-ink entrance, reusable across
// any SVG host. The icon enters at announcement scale, ETCHES itself — DrawSVG
// draw-on over stroke clones of its own shapes, each in the color its shape
// draws with (the purple chalice etches in purple, the Null zero in four suit
// colors) — INKS in at the etch's golden cut, then PRESSES down to its rest.
//
// Graduated from declaration-reveal's beat 1 (its header tells that narrative):
// what was declaration-specific is now the caller's — the `rest` descriptor
// carries the host's resting opacity and any base transform the press must land
// on (the watermark's stylesheet centering parses to a px x/y under GSAP, so
// the caller passes the percent re-expression that cancels it). The component
// owns only the gesture: scale, etch, ink, press, and the wrap/unwrap
// canonicalization that leaves the rested SVG equal to the factory output.
//
// Two modes, selected by `strokeNative`:
//   - clone mode (default) — fill icons (pips, chalice, Null zero) don't draw,
//     so the etch is stroke CLONES of their shapes over an ink group that blooms
//     the fills in. This is what the declaration watermark and game-start pips use.
//   - stroke-native mode (`strokeNative: true`) — a stroke-based icon (gatehouse,
//     standards, position trio) is DrawSVG-native: it etches its OWN strokes with
//     no clones and no ink group. Mixed icons (a banner's stroke pole + filled
//     cloth) draw their strokes and bloom the leftover fills in the same gesture —
//     "pole, then cloth." Staged live in the etch museum (dev/etch-museum.js).
//
// One seam orthogonal to the mode:
//   - press-skip (game-start pips, opened) — `stampPress: false` ends the
//     gesture at the ink bloom: the icon inscribes without the announcement
//     press (pair with `stampScale: 1` so it etches at rest size).
import { gsap } from "../../vendor/gsap.js";
import { DrawSVGPlugin } from "../../vendor/gsap-drawsvg.js";

gsap.registerPlugin(DrawSVGPlugin);

export const ETCH_STAMP_DEFAULTS = {
  stampScale: 1.382, // why: 1 + 1/φ² — the golden announcement scale the icon etches at, center-stage; settles to 1
  etchS: 0.4, // play-test: each etch stroke's draw-on span
  etchStaggerS: 0.035, // play-test: gap between stroke starts — the inscription rhythm
  inkAtFrac: 0.618, // why: golden — the fill blooms at the etch's golden cut, strokes still finishing over it
  inkS: 0.4, // play-test: the fill's bloom
  stampSettleS: 0.5, // play-test: the press from announcement scale down to rest
  stampPressEase: "back.out(2.5)", // play-test: the impact character — presses through rest, recoils up
  stampPress: true, // when false, the gesture ends at the ink bloom — etch-only, no press (set stampScale:1 so the icon inscribes at rest size). The game-start pips' seam.
  strokeNative: false, // when true, the icon etches its OWN strokes (no clones, no ink group); leftover fills bloom in. For stroke-based icons (gatehouse, standards) — see the header. Default false: fill icons need the clones.
  etchWidth: 1.4, // why: viewBox units (icons live in ~40-unit boxes); thick enough to read at rest size, thin enough to stay an etch (clone mode only — native strokes keep their own width)
};

/**
 * Rearrange the host icon's SVG for the etch-and-ink entrance, returning the
 * pieces the timeline drives. The icon's own shapes move into an "ink" group
 * (faded in as one); stroke-only clones form the "etch" group DrawSVG draws on.
 * Both groups unwrap/remove at shedStampResidue, so the rested SVG equals the
 * factory output. The etch degrades honestly where the environment can't
 * measure SVG geometry (jsdom): ink + press still play.
 *
 * @param {HTMLElement} host the element wrapping the icon's `> svg`
 * @param {object} c ETCH_STAMP_DEFAULTS (+ caller overrides)
 */
export function prepareStamp(host, c) {
  const svg = host.querySelector(":scope > svg");
  if (svg == null) return null;
  return c.strokeNative ? prepareNativeStamp(svg) : prepareCloneStamp(svg, c);
}

// Clone mode: the icon's own shapes move into an "ink" group (faded in as one);
// stroke-only clones form the "etch" group DrawSVG draws on.
function prepareCloneStamp(svg, c) {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const ink = document.createElementNS(SVG_NS, "g");
  ink.classList.add("etch-stamp-ink");
  for (const child of [...svg.children]) {
    if (child.tagName.toLowerCase() !== "defs") ink.appendChild(child);
  }
  svg.appendChild(ink);
  const etch = document.createElementNS(SVG_NS, "g");
  etch.classList.add("etch-stamp-etch");
  etch.setAttribute("fill", "none");
  svg.appendChild(etch);
  return { svg, ink, etch, fills: [], strokes: etchStrokes(ink, etch, c) };
}

/**
 * Stroke-native mode: no clones, no reparenting (the icon's z-order is its
 * design — moving fills into a group would reorder them). The icon's own shapes
 * partition in place into the strokes DrawSVG draws on (fill:none paths/lines)
 * and the fills that bloom in (cloth, rubble, orbs). Where geometry can't be
 * measured (jsdom) the draw degrades to a whole-icon bloom, like clone mode's.
 */
function prepareNativeStamp(svg) {
  // skip shapes inside <defs> (clip-path / mask templates) — they're never drawn
  // directly, the same exclusion clone mode makes by not reparenting <defs>.
  const shapes = [
    ...svg.querySelectorAll("path, circle, ellipse, rect, line"),
  ].filter((s) => s.closest("defs") == null);
  const measurable = shapes.length > 0 && canMeasureSvg(shapes[0]);
  const strokes = [];
  const fills = [];
  for (const shape of shapes) {
    if (measurable && isStrokeShape(shape)) strokes.push(shape);
    else fills.push(shape);
  }
  return { svg, ink: null, etch: null, fills, strokes, native: true };
}

// A draw-on shape carries a visible stroke and no own fill — the svg.js `path`
// and `line` primitives (gatehouse walls, banner pole, the detail texture
// lines). A shape with a fill (cloth, rubble, an orb) blooms instead, its rim
// stroke carried along with it.
function isStrokeShape(shape) {
  const fill = shape.getAttribute("fill");
  const stroke = shape.getAttribute("stroke");
  return (
    (fill == null || fill === "none") && stroke != null && stroke !== "none"
  );
}

// Stroke clones of every drawable shape, appended FLAT to the etch group — so
// each clone carries the COMPOSED ancestor transform (the fallen chalice's
// rotate, a pip's translate/scale would otherwise be lost) and the paint its
// shape INHERITS (pips take fill from their <g>). A <path> needs real geometry
// measurement (getTotalLength / getBBox) — probe once and skip the etch
// entirely where it's absent (jsdom; DrawSVG measures the other primitives
// analytically but its init still probes getBBox on all of them).
function etchStrokes(ink, etch, c) {
  const shapes = [...ink.querySelectorAll("path, circle, ellipse, rect, line")];
  if (shapes.length === 0 || !canMeasureSvg(shapes[0])) return [];
  return shapes.map((shape) => {
    const clone = shape.cloneNode(false);
    clone.removeAttribute("opacity");
    clone.setAttribute("fill", "none");
    clone.setAttribute("stroke", etchColor(shape, ink));
    clone.setAttribute("stroke-width", c.etchWidth);
    clone.setAttribute("stroke-linecap", "round");
    clone.setAttribute("stroke-linejoin", "round");
    const t = composedTransform(shape, ink);
    if (t) clone.setAttribute("transform", t);
    etch.appendChild(clone);
    return clone;
  });
}

// The shape's transform composed with its ancestors' up to the ink group,
// outermost first — what the flat clone needs to land where the original draws.
function composedTransform(shape, ink) {
  const parts = [];
  for (let el = shape; el !== ink; el = el.parentElement) {
    const t = el.getAttribute("transform");
    if (t) parts.unshift(t);
  }
  return parts.join(" ");
}

function canMeasureSvg(shape) {
  try {
    shape.getBBox();
    return (
      shape.tagName.toLowerCase() !== "path" ||
      typeof shape.getTotalLength === "function"
    );
  } catch {
    return false;
  }
}

// A shape etches in its own color: its fill or stroke, inherited from the
// nearest ancestor that declares one (pip shapes inherit fill from their <g>;
// gradient URLs resolve within the same SVG), vellum as the last resort.
function etchColor(shape, ink) {
  for (
    let el = shape;
    el != null && el !== ink.parentElement;
    el = el.parentElement
  ) {
    const fill = el.getAttribute("fill");
    if (fill && fill !== "none") return fill;
    const stroke = el.getAttribute("stroke");
    if (stroke && stroke !== "none") return stroke;
  }
  return "#F1F5F9";
}

/**
 * The stamp's staged start: announcement scale, full presence (opacity 1 — the
 * REST it presses down to), fills hidden behind the etch, strokes undrawn. The
 * caller's `rest.baseTransform` is the transform the host must keep through the
 * gesture (a watermark centered by a stylesheet translate(-50%,-50%) parses to
 * a px x/y under GSAP, so the caller re-expresses it as xPercent/yPercent and
 * zeroes x/y to avoid a double shift); a host with no such transform omits it.
 *
 * @param {{opacity?: number, baseTransform?: object}} [rest]
 */
export function stageStamp(host, stamp, c, rest = {}) {
  gsap.set(host, {
    ...(rest.baseTransform ?? {}),
    scale: c.stampScale,
    opacity: 1,
  });
  if (stamp.ink) gsap.set(stamp.ink, { opacity: 0 }); // clone mode: one group
  for (const fill of stamp.fills) gsap.set(fill, { opacity: 0 }); // native mode: in place
  for (const stroke of stamp.strokes) {
    gsap.set(stroke, { drawSVG: "0% 0%" });
  }
}

/**
 * The Stamp on the master timeline: etch → ink at the etch's golden cut →
 * press down to rest (scale 1, the caller's rest opacity). Returns the press's
 * impact moment (the settle's end) — the caller's cause for whatever the impact
 * triggers (the declaration reveal's ignition wave).
 *
 * @param {{opacity?: number}} [rest] rest.opacity defaults to 1 (no fade); the
 *   declaration watermark passes its 0.45 stylesheet rest.
 */
export function addStamp(tl, host, stamp, c, rest = {}) {
  if (stamp == null) return 0;
  const drawEnd = addEtch(tl, stamp, c);
  const inkAt = drawEnd * c.inkAtFrac;
  addInk(tl, stamp, inkAt, c);
  const settleAt = Math.max(drawEnd, inkAt + c.inkS);
  // press-skip seam: an etch-only caller (game-start pips) inscribes without the
  // announcement press — the gesture ends at the ink bloom, no scale settle.
  if (c.stampPress === false) return settleAt;
  tl.to(
    host,
    {
      scale: 1,
      opacity: rest.opacity ?? 1, // why: the host's rest — pressed down to, then clearProps hands it back to the stylesheet
      duration: c.stampSettleS,
      ease: c.stampPressEase,
    },
    settleAt,
  );
  return settleAt + c.stampSettleS;
}

// The inscription: staggered draw-ons across the strokes (clones in clone mode,
// the icon's own strokes in native mode). In clone mode the whole etch group
// recedes once the ink takes over (the clones served their purpose); native mode
// has no clone group to shed — its strokes ARE the drawing. Returns the last
// draw-on's end (0 when the etch degraded away).
function addEtch(tl, stamp, c) {
  let drawEnd = 0;
  stamp.strokes.forEach((stroke, i) => {
    const at = i * c.etchStaggerS;
    tl.to(
      stroke,
      { drawSVG: "0% 100%", duration: c.etchS, ease: "power1.inOut" },
      at,
    );
    drawEnd = at + c.etchS;
  });
  if (stamp.etch && stamp.strokes.length > 0) {
    tl.to(
      stamp.etch,
      { opacity: 0, duration: c.inkS, ease: "power2.out" },
      drawEnd * c.inkAtFrac + c.inkS * 0.382,
    );
  }
  return drawEnd;
}

// The ink bloom: clone mode fades the whole ink group as one; native mode fades
// the leftover fills in place (no group — preserves the icon's z-order). A
// pure-stroke native icon has no fills and this is a no-op.
function addInk(tl, stamp, inkAt, c) {
  if (stamp.ink) {
    tl.to(
      stamp.ink,
      { opacity: 1, duration: c.inkS, ease: "power2.in" },
      inkAt,
    );
  } else if (stamp.fills.length > 0) {
    tl.to(
      stamp.fills,
      { opacity: 1, duration: c.inkS, ease: "power2.in" },
      inkAt,
    );
  }
}

/**
 * Return the icon to its factory shape and hand the host back to the stylesheet
 * (rest opacity, any centering transform). Clone mode unwraps the ink group
 * (children return to their original positions — the group sits where they left)
 * and drops the etch; native mode never reparented, so it just clears the GSAP
 * draw/opacity residue off the icon's own shapes.
 */
export function shedStampResidue(host, stamp) {
  gsap.killTweensOf([host, ...stamp.strokes, ...stamp.fills]);
  if (stamp.native) {
    const own = [...stamp.strokes, ...stamp.fills];
    gsap.set(own, { clearProps: "strokeDasharray,strokeDashoffset,opacity" });
    // clearProps empties the inline props but leaves an empty style attribute;
    // drop it only when nothing remains, so a factory inline style (a banner's
    // identity cloth fill) survives but GSAP-only residue is gone — the rested
    // SVG equals the factory output, the same invariant clone mode holds.
    for (const shape of own) {
      if (shape.style.length === 0) shape.removeAttribute("style");
    }
  } else {
    gsap.killTweensOf([stamp.ink, stamp.etch]);
    while (stamp.ink.firstChild) {
      stamp.svg.insertBefore(stamp.ink.firstChild, stamp.ink);
    }
    stamp.ink.remove();
    stamp.etch.remove();
  }
  gsap.set(host, { clearProps: "transform,opacity" });
}
